import { z } from "zod";

// Paridade com dc-not-empty do legado: razaoSocial, nomeFantasia e
// codigoUsuarioCliente são sempre obrigatórios; motivoCancelamento só é
// obrigatório quando situacaoAdmin === 'C' (mesma condição do
// `ng-show`/`ng-if` que também controla a exibição do campo no legado —
// tratado com `superRefine` porque a obrigatoriedade depende de outro campo).
// Nenhum outro campo tinha `dc-not-empty` no legado (nem situacaoAdmin,
// situacaoCs, plano, os flags "se..." ou seHomeCare, que nem tem campo em
// tela) — por isso ficam `.nullish()` aqui. Registros antigos podem não ter
// esses valores gravados; forçar obrigatoriedade no cliente só bloqueia o
// salvamento de dados que o próprio legado sempre aceitou como ausentes, sem
// ganho real (o valor "correto" de fallback, se existir, é responsabilidade
// do backend — o cliente não deve inventar um pra não sobrescrever dado real
// que só não veio no GET).
// O Select do Radix pode emitir "" (valor vazio) quando o form é resetado antes
// de os itens se registrarem. "" nunca é um valor válido de enum aqui — tratado
// como ausente, para não bloquear nem gravar lixo.
// O retorno mantém o tipo do schema original: sem isso o z.preprocess troca o
// tipo de entrada por `unknown` e o resolver do react-hook-form deixa de casar
// com os tipos do form (erros TS2322/TS2719 no `tsc -b` do build).
const vazioComoAusente = <T extends z.ZodTypeAny>(schema: T): T =>
  z.preprocess((valor) => (valor === "" ? undefined : valor), schema) as unknown as T;

export const instituicaoFormSchema = z
  .object({
    id: z.number().optional(),
    razaoSocial: z.string().min(1, "Informe a razão social."),
    nomeFantasia: z.string().min(1, "Informe o nome fantasia."),
    cnpj: z.string().optional(),
    emailFinanceiro: z.string().optional(),
    privadaFilantropica: vazioComoAusente(z.enum(["P", "V"]).optional()),
    seParteDeAssociacao: vazioComoAusente(z.enum(["S", "N"]).optional()),
    seSaoVicenteDePaulo: vazioComoAusente(z.enum(["S", "N"]).optional()),
    dataInicio: z.string().optional(),
    nomeContato: z.string().optional(),
    email: z.string().optional(),
    telefone: z.string().optional(),
    celular: z.string().optional(),
    cep: z.string().optional(),
    rua: z.string().optional(),
    bairro: z.string().optional(),
    municipio: z.string().optional(),
    estado: z.string().optional(),
    codigoUsuarioCliente: z.string().min(1, "Informe o código do usuário cliente."),
    situacaoAdmin: vazioComoAusente(z.enum(["A", "T", "I", "C", "E"]).nullish()),
    dataFimTrial: z.string().optional(),
    // Legado grava esse campo como número no banco; aceitar e converter para
    // string evita erro de validação num campo que é só consulta (disabled).
    diasTrial: z.coerce.string().optional(),
    motivoCancelamento: z.string().optional(),
    situacaoCs: vazioComoAusente(z.enum(["ON", "OG", "EX", "RE", "RC"]).nullish()),
    plano: vazioComoAusente(z.enum(["M", "S", "A"]).nullish()),
    dataRenovacaoPlano: z.string().optional(),
    seBloqueiaDataHoraEvolucao: vazioComoAusente(z.enum(["S", "N"]).nullish()),
    seMedicamentoGeral: vazioComoAusente(z.enum(["S", "N"]).nullish()),
    seHomeCare: vazioComoAusente(z.enum(["S", "N"]).nullish()),
    seControlarEstoque: vazioComoAusente(z.enum(["S", "N"]).nullish()),
    seAppLiberado: vazioComoAusente(z.enum(["S", "N"]).nullish()),
    seProgramacaoAutomatica: vazioComoAusente(z.enum(["S", "N"]).nullish()),
    quantidadeDiasProgramacao: z.number().min(2).max(15).nullish(),
    seProgramacaoChecagem: vazioComoAusente(z.enum(["S", "N"]).nullish()),
    quantidadeDiasProgramacaoChecagem: z.number().min(2).max(15).nullish(),
    seBloqueioFinanceiro: vazioComoAusente(z.enum(["S", "N"]).optional()),
    dataIgnorarBloqueio: z.string().optional(),
    origemCadastro: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.situacaoAdmin === "C" && !values.motivoCancelamento) {
      ctx.addIssue({ code: "custom", path: ["motivoCancelamento"], message: "Informe o motivo do cancelamento." });
    }
  });

export type InstituicaoFormValues = z.infer<typeof instituicaoFormSchema>;
